import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Divider,
  CircularProgress,
  Chip,
  Autocomplete,
} from '@mui/material';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { FiltrosActivos } from '../hooks/useCatalogo';
import { FiltrosDisponibles, CategoriaFiltroPublico } from '../types';
import { ArbolCategoriasProveedor } from './ArbolCategoriasProveedor';

interface FiltrosPanelProps {
  filtros: FiltrosActivos;
  filtrosDisponibles: FiltrosDisponibles | null;
  loadingFiltros: boolean;
  proveedorIds: number[];
  subcategoriasSeleccionadas: number[];
  productosCount: number;
  onChange: <K extends keyof FiltrosActivos>(key: K, value: FiltrosActivos[K]) => void;
  onSetProveedores: (ids: number[]) => void;
  onToggleSubcategoria: (id: number) => void;
  onToggleCategoriaCompleta: (ids: number[]) => void;
  onLimpiarSubcategorias: () => void;
  onLimpiar: () => void;
}

const hayFiltrosActivos = (filtros: FiltrosActivos, proveedorIds: number[]): boolean =>
  proveedorIds.length > 0 ||
  filtros.categoriaIds.length > 0 ||
  filtros.subcategoriaIds.length > 0 ||
  filtros.tags.length > 0 ||
  filtros.precioMin.trim() !== '' ||
  filtros.precioMax.trim() !== '' ||
  filtros.texto.trim() !== '' ||
  filtros.conStock ||
  filtros.orden !== 'nombre_asc';

export function FiltrosPanel({
  filtros,
  filtrosDisponibles,
  loadingFiltros,
  proveedorIds,
  subcategoriasSeleccionadas,
  productosCount,
  onChange,
  onSetProveedores,
  onToggleSubcategoria,
  onToggleCategoriaCompleta,
  onLimpiarSubcategorias,
  onLimpiar,
}: FiltrosPanelProps) {
  const categorias: CategoriaFiltroPublico[] = filtrosDisponibles?.categorias ?? [];
  const proveedores = filtrosDisponibles?.proveedores ?? [];
  const categoriasProveedor = filtrosDisponibles?.categoriasProveedor ?? null;

  const categoriasSeleccionadas = categorias.filter((c) =>
    filtros.categoriaIds.includes(c.id)
  );

  const subcategoriasDisponibles = categorias
    .filter((c) => filtros.categoriaIds.includes(c.id))
    .flatMap((c) => c.subcategorias);

  const subcategoriasSeleccionadasObj = subcategoriasDisponibles.filter((s) =>
    filtros.subcategoriaIds.includes(s.id)
  );

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        border: '1px solid rgba(160, 88, 142, 0.1)',
        borderRadius: 2,
        padding: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        position: 'sticky',
        top: 16,
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchIcon sx={{ color: '#a0588e' }} />
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, color: '#a0588e' }}
          >
            Filtros
          </Typography>
        </Box>
        <Chip
          data-testid="contador-resultados"
          label={`${productosCount} productos`}
          size="small"
          sx={{ backgroundColor: '#a0588e', color: 'white' }}
        />
      </Box>

      {hayFiltrosActivos(filtros, proveedorIds) && (
        <Button
          data-testid="btn-limpiar-filtros"
          variant="outlined"
          size="small"
          startIcon={<FilterListOffIcon />}
          onClick={onLimpiar}
          sx={{
            borderColor: '#a0588e',
            color: '#a0588e',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '0.75rem',
            '&:hover': { borderColor: '#8a4a78', backgroundColor: 'rgba(160, 88, 142, 0.05)' },
          }}
        >
          Limpiar filtros
        </Button>
      )}

      <Divider />

      {/* Filtro Proveedor — Autocomplete multiple */}
      <Box data-testid="filtro-proveedores-section">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <StorefrontIcon sx={{ fontSize: 14, color: '#a0588e' }} />
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{ color: '#a0588e', fontFamily: 'Poppins, sans-serif', textTransform: 'uppercase' }}
          >
            Proveedores
          </Typography>
        </Box>
        {loadingFiltros ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="caption">Cargando…</Typography>
          </Box>
        ) : (
          <Autocomplete
            multiple
            size="small"
            options={proveedores}
            getOptionLabel={(p) => p.nombre}
            value={proveedores.filter((p) => proveedorIds.includes(p.id))}
            onChange={(_, selected) => onSetProveedores(selected.map((p) => p.id))}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            noOptionsText="Sin proveedores disponibles"
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={proveedorIds.length === 0 ? 'Seleccionar proveedores…' : ''}
                inputProps={{
                  ...params.inputProps,
                  'data-testid': 'input-proveedores',
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((p, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={p.id}
                  label={p.nombre}
                  size="small"
                  data-testid={`chip-proveedor-${p.id}`}
                />
              ))
            }
            data-testid="autocomplete-proveedores"
          />
        )}
      </Box>

      {/* Árbol Categorías Proveedor — visible only when ≥1 proveedor selected */}
      {proveedorIds.length > 0 && (
        <>
          <Divider />
          <Box data-testid="filtro-categoria-proveedor-section">
            {categoriasProveedor === null || categoriasProveedor.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
                Sin categorías disponibles
              </Typography>
            ) : (
              <ArbolCategoriasProveedor
                categorias={categoriasProveedor}
                subcategoriasSeleccionadas={subcategoriasSeleccionadas}
                onToggleSubcategoria={onToggleSubcategoria}
                onToggleCategoriaCompleta={onToggleCategoriaCompleta}
                onLimpiar={onLimpiarSubcategorias}
              />
            )}
          </Box>
        </>
      )}

      <Divider />

      {/* Filtro Categoría canónica — Autocomplete multiple */}
      <Box data-testid="filtro-categoria-canonica">
        <Typography
          variant="caption"
          fontWeight="bold"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}
        >
          Categoría
        </Typography>
        <Autocomplete
          multiple
          size="small"
          options={categorias}
          getOptionLabel={(c) => c.nombre}
          value={categoriasSeleccionadas}
          onChange={(_, selected) => {
            const nuevosIds = selected.map((c) => c.id);
            // Limpiar subcategorías huérfanas
            const subcatValidas = filtros.subcategoriaIds.filter((sid) =>
              selected.some((c) => c.subcategorias.some((s) => s.id === sid))
            );
            onChange('categoriaIds', nuevosIds);
            onChange('subcategoriaIds', subcatValidas);
          }}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          noOptionsText="Sin categorías disponibles"
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={filtros.categoriaIds.length === 0 ? 'Seleccionar categorías…' : ''}
              inputProps={{
                ...params.inputProps,
                'data-testid': 'input-categorias',
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((c, index) => (
              <Chip
                {...getTagProps({ index })}
                key={c.id}
                label={c.nombre}
                size="small"
                data-testid={`chip-categoria-${c.id}`}
              />
            ))
          }
          data-testid="autocomplete-categorias"
        />
      </Box>

      {/* Subcategorías canónicas — condicional */}
      {filtros.categoriaIds.length > 0 && subcategoriasDisponibles.length > 0 && (
        <>
          <Box data-testid="filtro-subcategoria-section">
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5 }}
            >
              Subcategoría
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={subcategoriasDisponibles}
              getOptionLabel={(s) => s.nombre}
              value={subcategoriasSeleccionadasObj}
              onChange={(_, selected) =>
                onChange('subcategoriaIds', selected.map((s) => s.id))
              }
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              noOptionsText="Sin subcategorías disponibles"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={filtros.subcategoriaIds.length === 0 ? 'Seleccionar subcategorías…' : ''}
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': 'input-subcategorias',
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((s, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={s.id}
                    label={s.nombre}
                    size="small"
                    data-testid={`chip-subcategoria-${s.id}`}
                  />
                ))
              }
              data-testid="autocomplete-subcategorias"
            />
          </Box>
        </>
      )}

      <Divider />

      {/* Filtro Tags */}
      <Box>
        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
          Tags
        </Typography>
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[] as string[]}
          value={filtros.tags}
          onChange={(_, value) => onChange('tags', value.map((v) => v.trim()).filter((v) => v.length > 0))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip variant="outlined" size="small" label={option} {...getTagProps({ index })} key={option} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Agregar tag…"
              inputProps={{ ...params.inputProps, 'data-testid': 'filtro-tags' }}
            />
          )}
          sx={{ mt: 0.5 }}
        />
      </Box>

      <Divider />

      {/* Filtro Precio */}
      <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
        Rango de precio (COP)
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Mínimo"
          size="small"
          type="number"
          value={filtros.precioMin}
          onChange={(e) => onChange('precioMin', e.target.value)}
          inputProps={{ min: 0, 'data-testid': 'filtro-precio-min' }}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Máximo"
          size="small"
          type="number"
          value={filtros.precioMax}
          onChange={(e) => onChange('precioMax', e.target.value)}
          inputProps={{ min: 0, 'data-testid': 'filtro-precio-max' }}
          sx={{ flex: 1 }}
        />
      </Box>

      <Divider />

      {/* Ordenamiento */}
      <FormControl fullWidth size="small">
        <InputLabel>Ordenar por</InputLabel>
        <Select
          value={filtros.orden}
          label="Ordenar por"
          onChange={(e) => onChange('orden', e.target.value as FiltrosActivos['orden'])}
          inputProps={{ 'data-testid': 'filtro-orden' }}
        >
          <MenuItem value="nombre_asc">Nombre A→Z</MenuItem>
          <MenuItem value="precio_asc">Precio ↑ (menor primero)</MenuItem>
          <MenuItem value="precio_desc">Precio ↓ (mayor primero)</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
}
